import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SeoChangeHistory } from "@/components/SeoChangeHistory";
import { Plus, Trash2, Copy, CopyPlus, KeyRound, Loader2, Check, Plug, Terminal, Activity, X, Globe } from "lucide-react";

const MCP_URL = `${process.env.REACT_APP_BACKEND_URL}/api/mcp`;

const SECTIONS = [
  { key: "properties", label: "Properties 360" },
  { key: "transactions", label: "Transaction Manager" },
  { key: "sales", label: "Sales Tracker" },
  { key: "people", label: "People" },
  { key: "agent_pages", label: "Agent Pages" },
  { key: "training", label: "Training" },
  { key: "procedures", label: "Office Procedures" },
  { key: "seo", label: "SEO / Site Files" },
];
const SECTION_LABEL = Object.fromEntries(SECTIONS.map((s) => [s.key, s.label]));

function Code({ children }) {
  return <code className="font-mono text-[13px] bg-[#0A0A0A] text-[#E7EDFB] px-2 py-1 rounded break-all">{children}</code>;
}

function scopeText(k) {
  const access = k.scope_access || "write";
  const secs = k.scope_sections || ["all"];
  const where = secs.includes("all") ? "All sections" : secs.map((s) => SECTION_LABEL[s] || s).join(", ");
  return `${access === "write" ? "Read & write" : "Read-only"} · ${where}${(k.scopes || []).includes("seo:write") ? " · seo:write" : ""}`;
}

export default function McpKeys() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [access, setAccess] = useState("write");
  const [allSections, setAllSections] = useState(true);
  const [chosen, setChosen] = useState([]);
  const [expiresDays, setExpiresDays] = useState(0);
  const [seoWrite, setSeoWrite] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [usageFor, setUsageFor] = useState(null);
  const [usage, setUsage] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setKeys((await api.get("/admin/mcp-keys")).data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/", { replace: true }); return; }
    if (user && user.role === "admin") load();
  }, [user, navigate]);

  const toggleSection = (key) =>
    setChosen((cur) => (cur.includes(key) ? cur.filter((s) => s !== key) : [...cur, key]));

  const create = async () => {
    if (!label.trim()) { toast.error("Give the key a label"); return; }
    const sections = allSections ? ["all"] : chosen;
    if (!allSections && sections.length === 0) { toast.error("Pick at least one section"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/mcp-keys", { label: label.trim(), access, sections, expires_in_days: expiresDays || null, scopes: seoWrite ? ["seo:write"] : [] });
      setNewKey(data.key);
      setCopied(false);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (keyId) => {
    try {
      await api.delete(`/admin/mcp-keys/${keyId}`);
      toast.success("Key revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleSeo = async (k) => {
    const on = !(k.scopes || []).includes("seo:write");
    try {
      await api.patch(`/admin/mcp-keys/${k.key_id}/scopes`, { scopes: on ? ["seo:write"] : [] });
      toast.success(on ? "seo:write added — the SEO write tools now appear in tools/list for this key" : "seo:write removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const clone = async (k) => {
    try {
      const { data } = await api.post(`/admin/mcp-keys/${k.key_id}/clone`);
      setNewKey(data.key);
      setCopied(false);
      toast.success("Key cloned with the same scope — copy the new key above");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openUsage = async (k) => {
    setUsageFor(k);
    setUsageLoading(true);
    setUsage([]);
    try {
      setUsage((await api.get(`/admin/mcp-keys/${k.key_id}/usage`)).data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUsageLoading(false);
    }
  };

  const copy = async (text, setFlag) => {
    try {
      await navigator.clipboard.writeText(text);
      if (setFlag) setFlag(true);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

  const openNew = () => { setLabel(""); setAccess("write"); setAllSections(true); setChosen([]); setExpiresDays(0); setOpen(true); };

  const isExpired = (k) => k.expires_at && new Date(k.expires_at) < new Date();

  return (
    <div data-testid="mcp-keys-page">
      <PageHeader
        title="API & Integrations"
        subtitle="Generate API keys so Claude (Desktop or web) and tools like Muse.ai can securely read and update your Command Center data. Scope each key to the exact sections and access it needs."
        action={
          <button
            data-testid="mcp-generate-button"
            onClick={openNew}
            className="inline-flex items-center gap-2 bg-[#1F3C88] hover:bg-[#152A61] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200"
          >
            <Plus className="w-4 h-4" /> Generate key
          </button>
        }
      />

      {newKey && (
        <div data-testid="mcp-new-key-banner" className="mb-8 bg-[#FBF2DE] border border-[#E7D9AF] rounded-lg p-5">
          <div className="flex items-center gap-2 text-[#8A6D1B] font-semibold text-sm mb-2">
            <KeyRound className="w-4 h-4" /> Copy your new key now — it won't be shown again
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Code>{newKey}</Code>
            <button
              data-testid="mcp-copy-new-key"
              onClick={() => copy(newKey, setCopied)}
              className="inline-flex items-center gap-1.5 bg-[#1F3C88] hover:bg-[#152A61] text-white px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
            </button>
            <button onClick={() => setNewKey(null)} className="text-xs text-[#8A6D1B] underline">Dismiss</button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div className="bg-white border border-[#E5E5E2] rounded-lg p-6">
          <div className="flex items-center gap-2 font-serif text-xl text-[#0A0A0A] mb-3">
            <Plug className="w-5 h-5 text-[#F7941D]" /> Connect Claude
          </div>
          <ol className="list-decimal list-inside space-y-2 text-sm text-[#575755]">
            <li>In Claude Desktop or Claude.ai, open <strong>Settings → Connectors → Add custom connector</strong>.</li>
            <li>Set the remote MCP server URL to:</li>
          </ol>
          <div className="my-3"><Code>{MCP_URL}</Code></div>
          <ol start={3} className="list-decimal list-inside space-y-2 text-sm text-[#575755]">
            <li>Under <strong>Request headers / Authentication</strong>, add header <Code>Authorization</Code> with value <Code>Bearer &lt;your-key&gt;</Code>.</li>
            <li>Save, then ask Claude to "list my properties" to confirm. Claude only sees tools your key is scoped for.</li>
          </ol>
        </div>

        <div className="bg-white border border-[#E5E5E2] rounded-lg p-6">
          <div className="flex items-center gap-2 font-serif text-xl text-[#0A0A0A] mb-3">
            <Terminal className="w-5 h-5 text-[#F7941D]" /> Muse.ai / scripts (REST)
          </div>
          <p className="text-sm text-[#575755] mb-3">
            The same key secures a plain REST API. Pass it as <Code>Authorization: Bearer &lt;key&gt;</Code> or <Code>x-api-key: &lt;key&gt;</Code>.
          </p>
          <div className="space-y-2 text-sm text-[#575755]">
            <div><Code>GET /api/ext/properties</Code> — list listings</div>
            <div><Code>GET /api/ext/transactions</Code> — list transactions</div>
            <div><Code>GET /api/ext/sales/summary</Code> — sales totals</div>
            <div><Code>GET /api/ext/people</Code> — directory</div>
            <div><Code>PATCH /api/ext/seo/agent-pages/&#123;username&#125;</Code> — SEO fields (needs seo:write)</div>
            <div><Code>/api/ext/seo/llms-txt · robots-txt · browse · site · redirects · changes</Code> — site SEO + change log</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E2] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E5E5E2] font-serif text-lg text-[#0A0A0A]">Active &amp; revoked keys</div>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#1F3C88]" /></div>
        ) : keys.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#9a978f]">No keys yet. Generate one to connect Claude or Muse.ai.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-[0.15em] uppercase text-[#9a978f] border-b border-[#E5E5E2]">
                  <th className="px-6 py-3 font-semibold">Label</th>
                  <th className="px-6 py-3 font-semibold">Scope</th>
                  <th className="px-6 py-3 font-semibold">Created by</th>
                  <th className="px-6 py-3 font-semibold">Last used</th>
                  <th className="px-6 py-3 font-semibold">Status</th>
                  <th className="px-6 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.key_id} data-testid={`mcp-key-row-${k.key_id}`} className="border-b border-[#F0EFEC] last:border-0">
                    <td className="px-6 py-4">
                      <div className="font-medium text-[#0A0A0A]">{k.label}</div>
                      <div className="font-mono text-[11px] text-[#9a978f]">{k.key_id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[#575755]">{scopeText(k)}</div>
                      {k.expires_at && (
                        <div className={`text-[11px] mt-0.5 ${isExpired(k) ? "text-[#992B2B]" : "text-[#9a978f]"}`}>
                          {isExpired(k) ? "Expired " : "Expires "}{fmt(k.expires_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[#575755]">{k.created_by_email || "—"}</td>
                    <td className="px-6 py-4 text-[#575755]">{fmt(k.last_used_at)}</td>
                    <td className="px-6 py-4">
                      {!k.active ? (
                        <StatusBadge tone="gray">Revoked</StatusBadge>
                      ) : isExpired(k) ? (
                        <StatusBadge tone="amber">Expired</StatusBadge>
                      ) : (
                        <StatusBadge tone="green">Active</StatusBadge>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-4">
                        {k.active && !isExpired(k) && (
                          <button
                            data-testid={`mcp-seo-toggle-${k.key_id}`}
                            onClick={() => toggleSeo(k)}
                            title="Allow or block SEO write tools for this key"
                            className={`inline-flex items-center gap-1.5 text-xs font-semibold ${(k.scopes || []).includes("seo:write") ? "text-[#2E5C47] hover:text-[#1d3d2f]" : "text-[#575755] hover:text-[#0A0A0A]"}`}
                          >
                            <Globe className="w-3.5 h-3.5" /> {(k.scopes || []).includes("seo:write") ? "SEO write on" : "Allow SEO write"}
                          </button>
                        )}
                        <button
                          data-testid={`mcp-clone-${k.key_id}`}
                          onClick={() => clone(k)}
                          title="Clone with the same scope & expiry window"
                          className="inline-flex items-center gap-1.5 text-[#575755] hover:text-[#0A0A0A] text-xs font-semibold"
                        >
                          <CopyPlus className="w-3.5 h-3.5" /> Clone
                        </button>
                        <button
                          data-testid={`mcp-activity-${k.key_id}`}
                          onClick={() => openUsage(k)}
                          className="inline-flex items-center gap-1.5 text-[#1F3C88] hover:text-[#152A61] text-xs font-semibold"
                        >
                          <Activity className="w-3.5 h-3.5" /> Activity
                        </button>
                        {k.active && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`mcp-revoke-${k.key_id}`} className="inline-flex items-center gap-1.5 text-[#992B2B] hover:text-[#7a1f1f] text-xs font-semibold">
                                <Trash2 className="w-3.5 h-3.5" /> Revoke
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Revoke this key?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Any Claude connector or script using <strong>{k.label}</strong> will immediately lose access. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction data-testid={`mcp-revoke-confirm-${k.key_id}`} onClick={() => revoke(k.key_id)} className="bg-[#992B2B] hover:bg-[#7a1f1f]">Revoke</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SeoChangeHistory />

      {/* Generate dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate API key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1">Label</label>
              <input
                data-testid="mcp-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Claude Desktop — Matt"
                className="w-full border border-[#D8D8D4] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3C88]"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1">Access</label>
              <div className="flex gap-2">
                {[["read", "Read-only"], ["write", "Read & write"]].map(([val, lab]) => (
                  <button
                    key={val}
                    type="button"
                    data-testid={`mcp-access-${val}`}
                    onClick={() => setAccess(val)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                      access === val ? "bg-[#1F3C88] text-white border-[#1F3C88]" : "bg-white text-[#575755] border-[#D8D8D4] hover:border-[#1F3C88]"
                    }`}
                  >
                    {lab}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1">Sections</label>
              <label className="flex items-center gap-2 text-sm text-[#575755] mb-2 cursor-pointer">
                <input type="checkbox" data-testid="mcp-sections-all" checked={allSections} onChange={(e) => setAllSections(e.target.checked)} className="accent-[#1F3C88]" />
                All sections
              </label>
              {!allSections && (
                <div className="grid grid-cols-2 gap-2 pl-1">
                  {SECTIONS.map((s) => (
                    <label key={s.key} className="flex items-center gap-2 text-sm text-[#575755] cursor-pointer">
                      <input type="checkbox" data-testid={`mcp-section-${s.key}`} checked={chosen.includes(s.key)} onChange={() => toggleSection(s.key)} className="accent-[#1F3C88]" />
                      {s.label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <label className="flex items-start gap-2 text-sm text-[#575755] cursor-pointer">
              <input type="checkbox" data-testid="mcp-scope-seo-write" checked={seoWrite} onChange={(e) => setSeoWrite(e.target.checked)} className="accent-[#1F3C88] mt-0.5" />
              <span><span className="font-medium text-[#0A0A0A]">Allow SEO changes (seo:write)</span><br /><span className="text-xs text-[#9a978f]">Lets this key edit agent page SEO fields, llms.txt, robots.txt, /browse, redirects and canonical host. Every change is logged and can be reverted.</span></span>
            </label>

            <p className="text-xs text-[#9a978f]">The raw key is shown only once after creation.</p>

            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-1">Expiry</label>
              <select
                data-testid="mcp-expiry-select"
                value={expiresDays}
                onChange={(e) => setExpiresDays(Number(e.target.value))}
                className="w-full border border-[#D8D8D4] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1F3C88]"
              >
                <option value={0}>Never expires</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </select>
              <p className="text-xs text-[#9a978f] mt-1">Expired keys stop working automatically and are marked revoked.</p>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm text-[#575755] hover:bg-[#F0EFEC]">Cancel</button>
              <button
                data-testid="mcp-create-confirm"
                onClick={create}
                disabled={creating}
                className="inline-flex items-center gap-2 bg-[#1F3C88] hover:bg-[#152A61] text-white px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Generate
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Usage / activity dialog */}
      <Dialog open={!!usageFor} onOpenChange={(v) => !v && setUsageFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#1F3C88]" /> Activity — {usageFor?.label}
            </DialogTitle>
          </DialogHeader>
          <div data-testid="mcp-usage-list" className="max-h-[60vh] overflow-y-auto">
            {usageLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1F3C88]" /></div>
            ) : usage.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#9a978f]">No activity recorded yet for this key.</div>
            ) : (
              <ul className="divide-y divide-[#F0EFEC]">
                {usage.map((e, i) => (
                  <li key={i} className="py-3 flex items-start gap-3">
                    <span className={`mt-0.5 shrink-0 ${e.status === "ok" ? "text-[#2E5C47]" : e.status === "denied" ? "text-[#B07D14]" : "text-[#992B2B]"}`}>
                      {e.status === "ok" ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[13px] text-[#0A0A0A]">{e.action}</span>
                        <StatusBadge tone={e.source === "mcp" ? "emerald" : "gray"}>{e.source === "mcp" ? "Claude / MCP" : "REST"}</StatusBadge>
                        {e.section && <span className="text-xs text-[#9a978f]">{SECTION_LABEL[e.section] || e.section}</span>}
                      </div>
                      {e.detail && <div className="mt-1 font-mono text-[11px] text-[#9a978f] break-all line-clamp-2">{e.detail}</div>}
                      {e.error && <div className="mt-1 text-[11px] text-[#992B2B]">{e.error}</div>}
                    </div>
                    <span className="shrink-0 text-xs text-[#9a978f] whitespace-nowrap">{fmt(e.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
